/**
 * OperationalRules Service
 * 운영 규칙 비즈니스 로직
 */

import type {
  AutoCheckoutRule,
  ReservationRule,
  SeatMovementRule,
  NotificationRule,
  OperatingHoursRule,
  OperationalSettings,
} from '../models/OperationalRules';

export class OperationalRulesService {
  /**
   * 자동 퇴실 규칙 검증
   */
  validateAutoCheckoutRule(rule: AutoCheckoutRule): { valid: boolean; message?: string } {
    if (rule.grace_period_minutes < 0) {
      return { valid: false, message: '유예 시간은 0분 이상이어야 합니다.' };
    }
    if (rule.force_checkout_after_minutes < rule.grace_period_minutes) {
      return { valid: false, message: '강제 퇴실 시간은 유예 시간보다 커야 합니다.' };
    }
    return { valid: true };
  }

  /**
   * 예약 규칙 검증
   */
  validateReservationRule(rule: ReservationRule): { valid: boolean; message?: string } {
    if (rule.max_advance_hours < 1) {
      return { valid: false, message: '최대 사전 예약 시간은 1시간 이상이어야 합니다.' };
    }
    if (rule.max_daily_reservations < 1) {
      return { valid: false, message: '일일 최대 예약 횟수는 1회 이상이어야 합니다.' };
    }
    if (rule.free_cancellation_hours < 0) {
      return { valid: false, message: '무료 취소 가능 시간은 0시간 이상이어야 합니다.' };
    }
    return { valid: true };
  }

  /**
   * 좌석 이동 규칙 검증
   */
  validateSeatMovementRule(rule: SeatMovementRule): { valid: boolean; message?: string } {
    if (rule.same_zone_fee < 0 || rule.different_zone_fee < 0) {
      return { valid: false, message: '이동 수수료는 0원 이상이어야 합니다.' };
    }
    if (rule.max_movements_per_session < 0) {
      return { valid: false, message: '세션당 최대 이동 횟수는 0회 이상이어야 합니다.' };
    }
    return { valid: true };
  }

  /**
   * 알림 규칙 검증
   */
  validateNotificationRule(rule: NotificationRule): { valid: boolean; message?: string } {
    if (rule.checkout_warnings.some((w) => w < 0)) {
      return { valid: false, message: '경고 시간은 0분 이상이어야 합니다.' };
    }
    if (rule.reservation_reminders.some((r) => r < 0)) {
      return { valid: false, message: '리마인더 시간은 0분 이상이어야 합니다.' };
    }
    return { valid: true };
  }

  /**
   * 운영시간 규칙 검증
   */
  validateOperatingHoursRule(rule: OperatingHoursRule): { valid: boolean; message?: string } {
    // 시간 형식 검증 (HH:MM)
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

    if (!timeRegex.test(rule.weekday_open) || !timeRegex.test(rule.weekday_close)) {
      return { valid: false, message: '평일 운영시간 형식이 올바르지 않습니다. (HH:MM)' };
    }

    if (!timeRegex.test(rule.weekend_open) || !timeRegex.test(rule.weekend_close)) {
      return { valid: false, message: '주말 운영시간 형식이 올바르지 않습니다. (HH:MM)' };
    }

    // 날짜 구분 기준 시간 검증 (0-23)
    if (rule.day_boundary_hour < 0 || rule.day_boundary_hour > 23) {
      return { valid: false, message: '날짜 구분 기준 시간은 0-23 사이여야 합니다.' };
    }

    return { valid: true };
  }

  /**
   * 기본 운영 설정 생성
   */
  createDefaultSettings(): OperationalSettings {
    return {
      auto_checkout: {
        enabled: true,
        grace_period_minutes: 30,
        warning_minutes: [10, 5],
        force_checkout_after_minutes: 60,
      },
      reservation: {
        enabled: true,
        max_advance_hours: 24,
        min_advance_minutes: 30,
        no_show_timeout_minutes: 15,
        free_cancellation_hours: 2,
        max_daily_reservations: 3,
      },
      seat_movement: {
        enabled: true,
        same_zone_fee: 0,
        different_zone_fee: 1000,
        premium_upgrade_policy: 'pay_difference',
        max_movements_per_session: 3,
      },
      notifications: {
        enabled: true,
        checkout_warnings: [30, 10, 5],
        reservation_reminders: [60, 30],
        send_sms: false,
        send_push: true,
        send_email: false,
      },
      operating_hours: {
        enabled: true,
        weekday_open: '09:00',
        weekday_close: '22:00',
        weekend_open: '10:00',
        weekend_close: '18:00',
        day_boundary_hour: 3,
        description: '날짜 구분 기준 시간 (예: 26일 데이터 조회 시 27일 03:00까지 포함)',
      },
    };
  }

  /**
   * 경고 시간 포맷팅
   */
  formatWarningTimes(minutes: number[]): string {
    return minutes.map((m) => `${m}분`).join(', ');
  }

  /**
   * 시간 단위 변환 (분 → 시간)
   */
  minutesToHours(minutes: number): number {
    return Math.floor(minutes / 60);
  }

  /**
   * 시간 단위 변환 (시간 → 분)
   */
  hoursToMinutes(hours: number): number {
    return hours * 60;
  }

  /**
   * 금액 포맷팅
   */
  formatCurrency(amount: number): string {
    return `${amount.toLocaleString()}원`;
  }
}

export const operationalRulesService = new OperationalRulesService();
